import React, { useState, useContext, useEffect, useCallback, useRef } from "react";
import { LocationContext } from "../../../context/LocationContext";
import { RequestList } from "./RequestList";
import { RequestContext } from "../../../context/RequestContext";
import { SubmitRequest } from "./SubmitRequest";
import { PtPlanAndPostalCode } from "./PtPlanAndPostalCode";
import { PtTrainerMatchLoader } from "./PtTrainerMatchLoader";
import { QuickSignIn } from "./QuickSignIn";
import { SystemError } from "../../error/SystemError";
import Script from 'next/script'
import { ServiceDateTime } from "../../commons/ServiceDateTime";
import { ServiceCriteria } from "../../commons/ServiceCriteria";
import { FitnessTrainerCriteria } from "../service-category/personal-trainer/FitnessTrainerCriteria";
import { closeModal } from "../../../service/modal-service";
import { PtQuickSignIn } from "./PtQuickSignIn";
import { BookingSheet } from "../../commons/booking/BookingSheet";
import { useStepStack } from "../../../utils/useStepStack";
import { bookingServiceLabel } from "../../../utils/booking-labels";
import { getMarketByName, hasFeature, localiseTerms } from "../../../utils/markets";
import { formatMoney } from "../../../utils/format-money";
import { computePackagePrice } from "../../../utils/pt-package-price";

// Booking form on the public (logged-out) pages. Same shell and step behaviour
// as ServicesModal on /home (components/commons/booking/BookingSheet.js,
// utils/useStepStack.js); the steps differ because a visitor also gives a
// postal code and signs up.
//
// Steps: 1 service list, 2 questions, 3 date/time, 4 address (or the trainer
// plan + postal code), 5 sign in (or the trainer match loader), 6 trainer
// sign in.
//
// asSheet: a bottom sheet over the page, for pages that open the form from a
// button. Without it the form is a card in the page (the personal trainer
// hero, /services/[countryCd]).
export const Services = (props) => {
  const requestCategorySettings = props.requestCategorySettings;
  const countryDescription = props.countryDescription
  const setRequestForm = props.setRequestForm
  const isQuickBook = props.isQuickBook
  const isCustomBook = props.isCustomBook
  const asSheet = props.asSheet

  // Derive businessType synchronously from props to determine initial section before render
  const derivedBusinessType = requestCategorySettings?.length > 0
    ? requestCategorySettings[0].businessType
    : null;

  // Compute the correct initial section synchronously to avoid layout shift
  // This replaces the post-render useEffect redirects that caused visible flicker
  const computeInitialSection = () => {
    // isQuickBook: skip to section 3 (job) or section 4 (non-job, non-hybrid)
    if (isQuickBook && derivedBusinessType && derivedBusinessType !== "hybrid") {
      if (derivedBusinessType === "job") return 3;
      return 4;
    }
    // isCustomBook: skip directly to section 4
    if (isCustomBook) return 4;
    // requestType provided: start at section 2 (criteria)
    if (props.requestType) return 2;
    // Default: section 1 (service list)
    return 1;
  };

  // Earlier steps stay mounted (hidden) while in the stack, so Back returns
  // to them with their answers.
  const { current: section, go, back, isMounted } = useStepStack(computeInitialSection())

  const isFirstRender = useRef(true);

  const [ matchedTrainer, setMatchedTrainer ] = useState(null);

  const [ isSystemError, setIsSystemError ] = useState(false);
  const [ errorContent, setErrorContent ]  = useState({'header': "", 'body': ""});

  // Summary appended to the remarks by the address step; outlives that step
  // so Back and forward again does not append it twice.
  const appendedSummaryRef = useRef("")

  const { requestType, requestCriteria, setRequestType, businessType, setBusinessType, setServiceItems,
    selectedPackage, serviceDate, startTime, requestDescription, setRequestDescription } = useContext(RequestContext);
  const { countryCd, countryCurrCd, countryName, location } = useContext(LocationContext)

  // Use the prop requestType as a fallback so SSR and client hydration agree.
  // The context requestType is only set client-side via useEffect, so during SSR
  // it is still "" and the wrong component (e.g. ServiceCriteria) would render.
  const effectiveRequestType = requestType || props.requestType;
  const isTrainer = effectiveRequestType === "Fitness Trainer"

  // Countries without trainers on the platform yet (utils/markets.js
  // features.trainerMatch) skip the trainer match: after the plan step the
  // plain QuickSignIn submits the request and ops matches a trainer by hand.
  const market = getMarketByName(countryName)
  const trainerMatch = !isTrainer || hasFeature(market, "trainerMatch")

  // The inline card sits in the page, so bring the page back to it; the sheet
  // scrolls its own body (BookingSheet scrollKey).
  const scrollPageTop = useCallback(() => {
    if (!asSheet) globalThis.scrollTo({ top: 0 });
  }, [asSheet])

  // Closes the form however the page opened it (openModal, plain state or
  // ?requestForm); see closeModal in service/modal-service.js.
  const closeForm = useCallback(() => {
    if (setRequestForm) closeModal(setRequestForm)
  }, [setRequestForm])

  // Back: previous step, or close when this was the first one. The date step
  // used to call dismissModal(), which left the page when the form had been
  // opened without a history entry.
  const goBack = useCallback(() => {
    if (!back()) closeForm()
  }, [back, closeForm])

  // The trainer match loader is not a step to go back to: sign in replaces it.
  const goToSection6 = useCallback(() => {
    scrollPageTop()
    go(6, { replace: true })
  }, [go, scrollPageTop])

  // QuickSignIn uploads a plain request and knows nothing about packages, so
  // without the trainer match a chosen package is written into the remarks.
  const packageNote = useCallback(() => {
    if (!selectedPackage) return ""
    const cd = market?.countryCd ?? "sg"
    const price = computePackagePrice(selectedPackage, serviceDate, startTime, requestCriteria, { applyOnlineDiscount: true, countryCd: cd })
    const tier = selectedPackage.tier ? `${selectedPackage.tier} ` : ""
    return `Package: ${tier}${selectedPackage.packageName}, ${selectedPackage.totalServices} sessions, ${formatMoney(price.totalCost, cd)}`
  }, [selectedPackage, market, serviceDate, startTime, requestCriteria])

  const goToSection5 = useCallback(() => {
    if (!trainerMatch) {
      const note = packageNote()
      if (note && !(requestDescription || "").includes(note)) {
        setRequestDescription(requestDescription ? `${requestDescription}\n${note}` : note)
      }
    }
    scrollPageTop()
    go(5)
  }, [go, scrollPageTop, trainerMatch, packageNote, requestDescription, setRequestDescription])

  const goToSection4 = useCallback(() =>{
    scrollPageTop()
    go(4)
  }, [go, scrollPageTop])

  const hasDateStep = (businessType || derivedBusinessType) === "job" ||
    ((businessType || derivedBusinessType) === "hybrid" && isQuickBook)

  const goToSection3 = useCallback(() => {
    if(requestCategorySettings[0].businessType === "job" || (requestCategorySettings[0].businessType === "hybrid" && isQuickBook)){
      scrollPageTop()
      go(3)
    } else {
      goToSection4()
    }
  }, [goToSection4, requestCategorySettings, isQuickBook, go, scrollPageTop])

  // "Start over at the questions". The sign-in steps call it when the request
  // data has expired; quick and custom bookings have no questions here, so
  // it closes the form instead (the page's picker is the start for those).
  const goToSection2 = useCallback(() =>{
    setServiceItems([])
    go(2)
    if(isCustomBook || isQuickBook){
      closeForm()
    }
  }, [setServiceItems, go, isCustomBook, isQuickBook, closeForm])

  const goToSection1 = useCallback(() => {
    go(1)
  }, [go])

  useEffect(() => {
    // Always clear the first-render flag
    const isFirstMount = isFirstRender.current;
    isFirstRender.current = false;

    if(requestCategorySettings?.length == 0){
        return;
    }
    // Always sync context values
    setBusinessType(requestCategorySettings[0].businessType)
    if(props.requestType) {
        setRequestType(props.requestType);
    }

    // Skip section navigation on first mount — initial section already computed synchronously
    if (isFirstMount) return;

    // On subsequent updates (e.g. requestCategorySettings changes), navigate as before
    if(props.requestType) {
        go(2)
    }else{
      goToSection1()
    }

  }, [requestCategorySettings, setRequestType, props.requestType, setBusinessType, go, goToSection1])

  // On subsequent renders: if isQuickBook becomes true while on section1/2, advance forward.
  // A jump, not a step: Back from there closes rather than returning to questions
  // the customer never answered.
  // Note: On first render the correct section is already set, so (section1 || section2) is false
  // when isQuickBook applies, preventing a redundant redirect.
  useEffect(() => {
      if(!businessType && requestCriteria.length == 0) return
      if(isQuickBook && (section === 1 || section === 2) && businessType != "hybrid"){
          if (businessType === "job") go(3, { replace: true })
          else go(4, { replace: true })
      }
  }, [isQuickBook, section, go, businessType, requestCriteria])

  // On subsequent renders: if isCustomBook becomes true while on section1, advance forward
  // Note: On first render section1 is already false when isCustomBook applies.
  useEffect(() => {
    if(isCustomBook && section === 1){
        go(4, { replace: true })
    }
  }, [isCustomBook, section, go])

  const initializeFB = () => {
      FB.init({
          appId: '191533927996140',
          cookie: true,
          xfbml: true,  // parse social plugins on this page
          version: 'v7.0' // use graph api version 2.8
      });
  };

  // Step indicator. The first step may have happened on the page (a quick
  // book card, the aircon picker) before the form opened.
  const firstStepLabel = isMounted(2) || (!isQuickBook && !isCustomBook) ? "Details"
    : isCustomBook ? "Services & time"
    : "Service"
  const steps = isTrainer
    ? ["Details", ...(hasDateStep ? ["Date & time"] : []), "Plan & location", "Sign in"]
    : [firstStepLabel, ...(hasDateStep && !isCustomBook ? ["Date & time"] : []), "Address", "Sign in"]
  const stepIndex = section === 2 ? 0
    : section === 3 ? steps.indexOf("Date & time")
    : section === 4 ? steps.indexOf(isTrainer ? "Plan & location" : "Address")
    : section === 5 ? steps.indexOf(isTrainer && trainerMatch ? "Plan & location" : "Sign in")
    : section === 6 ? steps.indexOf("Sign in")
    : -1
  const serviceLabel = localiseTerms(bookingServiceLabel(effectiveRequestType), market)
  const title = section === 1 || !serviceLabel ? "Request a service" : `Book ${serviceLabel}`

  // Steps stay mounted while in the stack; only the current one shows.
  const stepClass = (step) => section === step ? "" : "hidden"

  return (
    <>
      <BookingSheet
        title={title}
        steps={steps}
        stepIndex={stepIndex}
        onClose={setRequestForm ? closeForm : undefined}
        inline={!asSheet}
        scrollKey={section}
      >
          {isMounted(1) && (
            <div className={props.requestType ? "hidden" : stepClass(1)}>
                <RequestList requestCategorySettings={requestCategorySettings}></RequestList>
            </div>
          )}

          {isMounted(2) && (
            <div className={stepClass(2)}>
                {isTrainer ? (
                  <FitnessTrainerCriteria
                      requestCategorySettings={requestCategorySettings}
                      goToSection3={goToSection3}
                      setRequestForm={setRequestForm}
                  />

                ) : (
                  <ServiceCriteria
                      requestCategorySettings={requestCategorySettings}
                      goToSection3={goToSection3}
                      setRequestForm={setRequestForm}
                      location={location}
                      countryCd={countryCd}
                  />
                )}
            </div>
          )}

          {isMounted(3) && (
            <div className={stepClass(3)}>
              <ServiceDateTime
                    goBack={goBack}
                    goToSection4={goToSection4}
                    countryName={countryName}
                    countryCurrCd={countryCurrCd}
              ></ServiceDateTime>
            </div>
          )}

          {isMounted(4) && (
            <div className={stepClass(4)}>
              {isTrainer ? (
                <PtPlanAndPostalCode goToSection2={goToSection2} goToSection5={goToSection5} goBack={goBack} />

              ) : (
                <SubmitRequest goToSection2={goToSection2} goToSection5={goToSection5} goToSection1={goToSection1} setRequestForm={setRequestForm} goBack={goBack} appendedSummaryRef={appendedSummaryRef} />
              )}
            </div>
          )}

          {isMounted(5) && (
            <div className={stepClass(5)}>
              {isTrainer && trainerMatch ? (
                <PtTrainerMatchLoader setMatchedTrainer={setMatchedTrainer}
                  onComplete={() => goToSection6()}
                />
              ) : (
                <QuickSignIn requestCategorySettings={requestCategorySettings} goToSection2={goToSection2} goBack={goBack} variant="light"/>
              )}
            </div>
          )}

          {isMounted(6) && (
            <div className={stepClass(6)}>
              <PtQuickSignIn
                requestCategorySettings={requestCategorySettings}
                goToSection2={goToSection2}
                goBack={goBack}
                matchedTrainer={matchedTrainer}
              />
            </div>
          )}

          {countryDescription &&
              <div className='p-6 space-y-5 max-w-5xl mx-auto text-sm leading-7 text-gray-500'>
                  <p>{countryDescription}</p>
                  <p className='wrap-break-word whitespace-pre-wrap'>
                      We aim to help people in {countryName} in finding the right service provider to fit their budget and other requirements.
                  </p>
              </div>
          }
          {isSystemError && <SystemError errorContent={errorContent} setIsSystemError={setIsSystemError}/>}
      </BookingSheet>

      <>
        <Script
          id="google-login-js"
          src="https://accounts.google.com/gsi/client"
          strategy="lazyOnload"
          onLoad={() => {
              //initializeGoogleLogin();
        }}
        />
        <Script
          id="fb-login-js"
          src="//connect.facebook.net/en_US/sdk.js"
          strategy="lazyOnload"
          onLoad={() => {
              initializeFB();
          }}
        />
        <Script
            id="google-tagmanager-js"
            src="https://www.googletagmanager.com/gtag/js?id=AW-823304067"
            strategy="lazyOnload"
            onLoad={() => {
                window.dataLayer = window.dataLayer || [];
                function gtag(){dataLayer.push(arguments);}
                gtag('js', new Date());

                gtag('config', 'AW-823304067');

                window.gtag = gtag;
            }}
        />
        <Script id="facebook-tracking" strategy="lazyOnload">
            {`!function(f,b,e,v,n,t,s)
                {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
                n.callMethod.apply(n,arguments):n.queue.push(arguments)};
                if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
                n.queue=[];t=b.createElement(e);t.async=!0;
                t.src=v;s=b.getElementsByTagName(e)[0];
                s.parentNode.insertBefore(t,s)}(window, document,'script',
                'https://connect.facebook.net/en_US/fbevents.js');
                fbq('init', '128634041116395');
                fbq('track', 'PageView');`}
        </Script>
      </>
    </>
  )
}
